import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import Tooltip from '@src/components/Tooltip';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { delimitNumber } from '@src/utils/number';
import useUserInfo from '@src/hooks/useTtsUser';
import { MAX_ROLLOVER_CYCLES } from '@src/constants/tts';

const UsedCredits = ({
  usedCredits,
  totalCredits,
  purchasedTopUpCredits = 0,
  isMobile,
  useLockCycle,
  useLockTopUp,
  isAutoRenewalPackage,
  hasNextResetCycle,
  totalCapturedRolloverCredits,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const { ttsUser } = useUserInfo();
  const hasTopUpCredits = Boolean(purchasedTopUpCredits);
  const [showInfoTopupAndRollover, setShowInfoTopupAndRollover] =
    useState(false);

  const isTopUpPackage = Boolean(purchasedTopUpCredits);
  const hasCaptureRolloverCredits = Boolean(totalCapturedRolloverCredits);

  const isLockedCycle = ttsUser?.studio?.cycle?.isLocked;
  const isLockedTopUp = ttsUser?.studio?.topUp?.isLocked;

  const maxRolloverCredits = (
    (ttsUser?.studio?.cycle?.creditsPerCycle || 0) * MAX_ROLLOVER_CYCLES
  ).toLocaleString(language);

  const maxRolloverCreditsNote =
    hasNextResetCycle || isAutoRenewalPackage
      ? t('maxRolloverCreditsNote', {
          maxRolloverCredits,
        })
      : '';

  const getTextTopUpandRollover = () => {
    if (hasTopUpCredits && hasCaptureRolloverCredits) {
      return 'topUpandCaptureRolloverCredits';
    }
    if (hasTopUpCredits) {
      return 'purchasedTopUpCredits';
    }
    if (hasCaptureRolloverCredits) {
      return 'capturedRolloverCreditsNote';
    }
    return '';
  };
  const renderTextTooltip = t(getTextTopUpandRollover(), {
    purchasedTopUpCredits: delimitNumber(purchasedTopUpCredits, language),
    totalCapturedRolloverCredits: delimitNumber(
      totalCapturedRolloverCredits,
      language,
    ),
  });
  const handleClickShowInfoTopupAndRollover = () => {
    if (isMobile) setShowInfoTopupAndRollover(true);
    return null;
  };

  const renderButtonGotIt = () => (
    <div className="used-credits-button-container">
      <Button
        className="button-got-it"
        variant="contained"
        onClick={() => setShowInfoTopupAndRollover(false)}
      >
        {t('gotItV2')}
      </Button>
    </div>
  );

  const renderNoteLockCredits = () => {
    if (purchasedTopUpCredits && !useLockTopUp)
      return (
        <Typography
          component="span"
          dangerouslySetInnerHTML={{
            __html: t('lockCycleCreditsTooltipMyPackageHasCharacters', {
              characters: delimitNumber(purchasedTopUpCredits, language),
            }),
          }}
        />
      );

    return t('lockCycleCreditsTooltipMyPackage');
  };

  const renderContainerLockCredits = () => {
    if (isLockedCycle && useLockCycle)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={renderNoteLockCredits()}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    if (isLockedTopUp && useLockTopUp)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={renderNoteLockCredits()}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    return <></>;
  };

  return (
    <>
      <Box className="used-credits">
        <Typography className="label">{t('usedCredits')}</Typography>
        <Box className="content-wrapper">
          <Typography className="content">
            {`${delimitNumber(usedCredits, language)}/${delimitNumber(
              totalCredits,
              language,
            )} ${isMobile ? '' : t('charactersLower')}`}
          </Typography>

          {(hasNextResetCycle ||
            isTopUpPackage ||
            isAutoRenewalPackage ||
            hasCaptureRolloverCredits) && (
            <Box className="tooltip">
              <Tooltip
                arrow
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {getTextTopUpandRollover() && (
                      <Typography sx={{ fontSize: '14px' }}>
                        {renderTextTooltip}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '14px' }}>
                      {maxRolloverCreditsNote}
                    </Typography>
                  </Box>
                }
                placement="left"
                componentsProps={{
                  tooltip: {
                    sx: {
                      padding: '12px',
                    },
                  },
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        textAlign: 'left !important',
                        marginLeft: '16px',
                      },
                    },
                  },
                }}
              >
                <IconButton onClick={handleClickShowInfoTopupAndRollover}>
                  <InfoIcon className="icon" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isMobile && renderContainerLockCredits()}
        </Box>
      </Box>
      <NotificationDialogV2
        showIcon={false}
        title={t('detailInfo')}
        description={t('topupAndRolloverNoteMobileWeb', {
          topUpCreditsNote: renderTextTooltip,
          maxRolloverCreditsNote,
        })}
        open={showInfoTopupAndRollover}
        onClose={() => setShowInfoTopupAndRollover(false)}
        actionComponent={renderButtonGotIt()}
      />
    </>
  );
};

export default UsedCredits;
